import { parsePhoneNumber } from "libphonenumber-js";
export interface CountryCode {
  code: string;
  dial_code: string;
  name: string;
  flag: string;
  currency: string;
}
export interface CountrySelectOption {
  value: string;
  label: string;
  code: string;
  icon: string;
  description: string;
  searchText: string;
}
export interface ParsedPhoneNumber {
  countryCode: string;
  nationalNumber: string;
  isValid: boolean;
}

export const parsePhoneNumberString = (
  phoneNumber: string,
): ParsedPhoneNumber => {
  const defaultResult: ParsedPhoneNumber = {
    countryCode: "+91",
    nationalNumber: phoneNumber.replace(/^\+/, ""),
    isValid: false,
  };
  if (!phoneNumber) {
    return defaultResult;
  }
  try {
    const normalizedNumber = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+${phoneNumber}`;
    const parsed = parsePhoneNumber(normalizedNumber);
    if (parsed) {
      const countryCallingCode = parsed.countryCallingCode;
      const nationalNumber = parsed.nationalNumber;
      return {
        countryCode: `+${countryCallingCode}`,
        nationalNumber: nationalNumber,
        isValid: parsed.isValid(),
      };
    }
  } catch (error) {
    return parsePhoneNumberManual(phoneNumber);
  }
  return defaultResult;
};

const parsePhoneNumberManual = (phoneNumber: string): ParsedPhoneNumber => {
  const cleanNumber = phoneNumber.replace(/^\+/, "");
  const sortedCodes = [...PhonenumberCodes].sort(
    (a, b) => b.dial_code.length - a.dial_code.length,
  );
  for (const country of sortedCodes) {
    const codeWithoutPlus = country.dial_code.replace(/^\+/, "");
    if (cleanNumber.startsWith(codeWithoutPlus)) {
      return {
        countryCode: country.dial_code,
        nationalNumber: cleanNumber.slice(codeWithoutPlus.length),
        isValid: true,
      };
    }
  }
  return {
    countryCode: "+91",
    nationalNumber: cleanNumber,
    isValid: false,
  };
};

export const formatPhoneNumber = (
  countryCode: string,
  nationalNumber: string,
): string => {
  return `${countryCode}${nationalNumber}`;
};
export const getCountryByDialCode = (
  dialCode: string,
): CountryCode | undefined => {
  const normalizedCode = dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
  return PhonenumberCodes.find((c) => c.dial_code === normalizedCode);
};

export type CurrencyCode =
  | "USD" // US Dollar ($)
  | "EUR" // Euro (€)
  | "GBP" // British Pound (£)
  | "INR" // Indian Rupee (₹)
  | "JPY" // Japanese Yen (¥)
  | "AUD" // Australian Dollar (A$)
  | "CAD" // Canadian Dollar (C$)
  | "CHF" // Swiss Franc (CHF)
  | "CNY" // Chinese Yuan (¥)
  | "AED"; // UAE Dirham (د.إ)

export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  INR: "₹",
  JPY: "¥",
  AUD: "A$",
  CAD: "C$",
  CHF: "CHF",
  CNY: "¥",
  AED: "د.إ",
};

export const getCurrencySymbol = (
  currencyCode?: CurrencyCode | string,
): string => {
  if (!currencyCode) return "$";
  return CURRENCY_SYMBOLS[currencyCode as CurrencyCode] || "$";
};

export const getCountryFlag = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const COUNTRY_SEARCH_ALIASES: Record<string, readonly string[]> = {
  AE: ["UAE", "Dubai", "Abu Dhabi", "Emirates"],
  GB: ["UK", "United Kingdom", "Great Britain", "England"],
  US: ["USA", "United States of America", "America"],
  KR: ["South Korea", "Republic of Korea"],
  KP: ["North Korea"],
  RU: ["Russia"],
  VN: ["Vietnam"],
  LA: ["Laos"],
  BO: ["Bolivia"],
  BN: ["Brunei"],
  IR: ["Iran"],
  SY: ["Syria"],
  TZ: ["Tanzania"],
  VE: ["Venezuela"],
};

function normalizeCountrySearchValue(value: string): string {
  return value.trim().toLowerCase();
}

export function getCountryAliases(country: CountryCode): string[] {
  return [...(COUNTRY_SEARCH_ALIASES[country.code] ?? [])];
}

export function normalizeCountryName(country?: string | null): string {
  const normalizedCountry = normalizeCountrySearchValue(country ?? "");
  if (!normalizedCountry) return "";

  const matchedCountry = PhonenumberCodes.find((entry) => {
    const searchableValues = [
      entry.name,
      entry.code,
      entry.dial_code,
      ...getCountryAliases(entry),
    ];
    return searchableValues.some(
      (value) => normalizeCountrySearchValue(value) === normalizedCountry,
    );
  });

  return matchedCountry?.name ?? country?.trim() ?? "";
}

export function getCountrySelectOptions(): CountrySelectOption[] {
  const seenNames = new Set<string>();

  return PhonenumberCodes.filter((country) => {
    if (seenNames.has(country.name)) return false;
    seenNames.add(country.name);
    return true;
  })
    .map((country) => {
      const aliases = getCountryAliases(country);
      return {
        value: country.name,
        label: country.name,
        code: country.code,
        icon: country.flag || getCountryFlag(country.code),
        description: [
          country.code,
          country.dial_code,
          aliases.length ? aliases.join(", ") : "",
        ]
          .filter(Boolean)
          .join(" · "),
        searchText: [
          country.name,
          country.code,
          country.dial_code,
          ...aliases,
        ].join(" "),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const PhonenumberCodes: CountryCode[] = [
  {
    code: "AF",
    dial_code: "+93",
    name: "Afghanistan",
    flag: "🇦🇫",
    currency: "AFN",
  },
  {
    code: "AX",
    dial_code: "+358",
    name: "Aland Islands",
    flag: "🇦🇽",
    currency: "EUR",
  },
  {
    code: "AL",
    dial_code: "+355",
    name: "Albania",
    flag: "🇦🇱",
    currency: "ALL",
  },
  {
    code: "DZ",
    dial_code: "+213",
    name: "Algeria",
    flag: "🇩🇿",
    currency: "DZD",
  },
  {
    code: "AS",
    dial_code: "+1684",
    name: "American Samoa",
    flag: "🇦🇸",
    currency: "USD",
  },
  {
    code: "AD",
    dial_code: "+376",
    name: "Andorra",
    flag: "🇦🇩",
    currency: "EUR",
  },
  {
    code: "AO",
    dial_code: "+244",
    name: "Angola",
    flag: "🇦🇴",
    currency: "AOA",
  },
  {
    code: "AI",
    dial_code: "+1264",
    name: "Anguilla",
    flag: "🇦🇮",
    currency: "XCD",
  },
  {
    code: "AQ",
    dial_code: "+672",
    name: "Antarctica",
    flag: "🇦🇶",
    currency: "USD",
  },
  {
    code: "AG",
    dial_code: "+1268",
    name: "Antigua and Barbuda",
    flag: "🇦🇬",
    currency: "XCD",
  },
  {
    code: "AR",
    dial_code: "+54",
    name: "Argentina",
    flag: "🇦🇷",
    currency: "ARS",
  },
  {
    code: "AM",
    dial_code: "+374",
    name: "Armenia",
    flag: "🇦🇲",
    currency: "AMD",
  },
  { code: "AW", dial_code: "+297", name: "Aruba", flag: "🇦🇼", currency: "AWG" },
  {
    code: "AU",
    dial_code: "+61",
    name: "Australia",
    flag: "🇦🇺",
    currency: "AUD",
  },
  {
    code: "AT",
    dial_code: "+43",
    name: "Austria",
    flag: "🇦🇹",
    currency: "EUR",
  },
  {
    code: "AZ",
    dial_code: "+994",
    name: "Azerbaijan",
    flag: "🇦🇿",
    currency: "AZN",
  },
  {
    code: "BS",
    dial_code: "+1242",
    name: "Bahamas",
    flag: "🇧🇸",
    currency: "BSD",
  },
  {
    code: "BH",
    dial_code: "+973",
    name: "Bahrain",
    flag: "🇧🇭",
    currency: "BHD",
  },
  {
    code: "BD",
    dial_code: "+880",
    name: "Bangladesh",
    flag: "🇧🇩",
    currency: "BDT",
  },
  {
    code: "BB",
    dial_code: "+1246",
    name: "Barbados",
    flag: "🇧🇧",
    currency: "BBD",
  },
  {
    code: "BY",
    dial_code: "+375",
    name: "Belarus",
    flag: "🇧🇾",
    currency: "BYN",
  },
  {
    code: "BE",
    dial_code: "+32",
    name: "Belgium",
    flag: "🇧🇪",
    currency: "EUR",
  },
  {
    code: "BZ",
    dial_code: "+501",
    name: "Belize",
    flag: "🇧🇿",
    currency: "BZD",
  },
  { code: "BJ", dial_code: "+229", name: "Benin", flag: "🇧🇯", currency: "XOF" },
  {
    code: "BM",
    dial_code: "+1441",
    name: "Bermuda",
    flag: "🇧🇲",
    currency: "BMD",
  },
  {
    code: "BT",
    dial_code: "+975",
    name: "Bhutan",
    flag: "🇧🇹",
    currency: "BTN",
  },
  {
    code: "BO",
    dial_code: "+591",
    name: "Bolivia",
    flag: "🇧🇴",
    currency: "BOB",
  },
  {
    code: "BA",
    dial_code: "+387",
    name: "Bosnia and Herzegovina",
    flag: "🇧🇦",
    currency: "BAM",
  },
  {
    code: "BW",
    dial_code: "+267",
    name: "Botswana",
    flag: "🇧🇼",
    currency: "BWP",
  },
  { code: "BR", dial_code: "+55", name: "Brazil", flag: "🇧🇷", currency: "BRL" },
  {
    code: "IO",
    dial_code: "+246",
    name: "British Indian Ocean Territory",
    flag: "🇮🇴",
    currency: "USD",
  },
  {
    code: "BN",
    dial_code: "+673",
    name: "Brunei Darussalam",
    flag: "🇧🇳",
    currency: "BND",
  },
  {
    code: "BG",
    dial_code: "+359",
    name: "Bulgaria",
    flag: "🇧🇬",
    currency: "BGN",
  },
  {
    code: "BF",
    dial_code: "+226",
    name: "Burkina Faso",
    flag: "🇧🇫",
    currency: "XOF",
  },
  {
    code: "BI",
    dial_code: "+257",
    name: "Burundi",
    flag: "🇧🇮",
    currency: "BIF",
  },
  {
    code: "KH",
    dial_code: "+855",
    name: "Cambodia",
    flag: "🇰🇭",
    currency: "KHR",
  },
  {
    code: "CM",
    dial_code: "+237",
    name: "Cameroon",
    flag: "🇨🇲",
    currency: "XAF",
  },
  { code: "CA", dial_code: "+1", name: "Canada", flag: "🇨🇦", currency: "CAD" },
  {
    code: "CV",
    dial_code: "+238",
    name: "Cape Verde",
    flag: "🇨🇻",
    currency: "CVE",
  },
  {
    code: "KY",
    dial_code: "+345",
    name: "Cayman Islands",
    flag: "🇰🇾",
    currency: "KYD",
  },
  {
    code: "CF",
    dial_code: "+236",
    name: "Central African Republic",
    flag: "🇨🇫",
    currency: "XAF",
  },
  { code: "TD", dial_code: "+235", name: "Chad", flag: "🇹🇩", currency: "XAF" },
  { code: "CL", dial_code: "+56", name: "Chile", flag: "🇨🇱", currency: "CLP" },
  { code: "CN", dial_code: "+86", name: "China", flag: "🇨🇳", currency: "CNY" },
  {
    code: "CX",
    dial_code: "+61",
    name: "Christmas Island",
    flag: "🇨🇽",
    currency: "AUD",
  },
  {
    code: "CC",
    dial_code: "+61",
    name: "Cocos (Keeling) Islands",
    flag: "🇨🇨",
    currency: "AUD",
  },
  {
    code: "CO",
    dial_code: "+57",
    name: "Colombia",
    flag: "🇨🇴",
    currency: "COP",
  },
  {
    code: "KM",
    dial_code: "+269",
    name: "Comoros",
    flag: "🇰🇲",
    currency: "KMF",
  },
  { code: "CG", dial_code: "+242", name: "Congo", flag: "🇨🇬", currency: "XAF" },
  {
    code: "CD",
    dial_code: "+243",
    name: "Congo, Democratic Republic",
    flag: "🇨🇩",
    currency: "CDF",
  },
  {
    code: "CK",
    dial_code: "+682",
    name: "Cook Islands",
    flag: "🇨🇰",
    currency: "NZD",
  },
  {
    code: "CR",
    dial_code: "+506",
    name: "Costa Rica",
    flag: "🇨🇷",
    currency: "CRC",
  },
  {
    code: "CI",
    dial_code: "+225",
    name: "Côte d'Ivoire",
    flag: "🇨🇮",
    currency: "XOF",
  },
  {
    code: "HR",
    dial_code: "+385",
    name: "Croatia",
    flag: "🇭🇷",
    currency: "EUR",
  },
  { code: "CU", dial_code: "+53", name: "Cuba", flag: "🇨🇺", currency: "CUP" },
  {
    code: "CY",
    dial_code: "+357",
    name: "Cyprus",
    flag: "🇨🇾",
    currency: "EUR",
  },
  {
    code: "CZ",
    dial_code: "+420",
    name: "Czech Republic",
    flag: "🇨🇿",
    currency: "CZK",
  },
  {
    code: "DK",
    dial_code: "+45",
    name: "Denmark",
    flag: "🇩🇰",
    currency: "DKK",
  },
  {
    code: "DJ",
    dial_code: "+253",
    name: "Djibouti",
    flag: "🇩🇯",
    currency: "DJF",
  },
  {
    code: "DM",
    dial_code: "+1767",
    name: "Dominica",
    flag: "🇩🇲",
    currency: "XCD",
  },
  {
    code: "DO",
    dial_code: "+1849",
    name: "Dominican Republic",
    flag: "🇩🇴",
    currency: "DOP",
  },
  {
    code: "EC",
    dial_code: "+593",
    name: "Ecuador",
    flag: "🇪🇨",
    currency: "USD",
  },
  { code: "EG", dial_code: "+20", name: "Egypt", flag: "🇪🇬", currency: "EGP" },
  {
    code: "SV",
    dial_code: "+503",
    name: "El Salvador",
    flag: "🇸🇻",
    currency: "USD",
  },
  {
    code: "GQ",
    dial_code: "+240",
    name: "Equatorial Guinea",
    flag: "🇬🇶",
    currency: "XAF",
  },
  {
    code: "ER",
    dial_code: "+291",
    name: "Eritrea",
    flag: "🇪🇷",
    currency: "ERN",
  },
  {
    code: "EE",
    dial_code: "+372",
    name: "Estonia",
    flag: "🇪🇪",
    currency: "EUR",
  },
  {
    code: "ET",
    dial_code: "+251",
    name: "Ethiopia",
    flag: "🇪🇹",
    currency: "ETB",
  },
  {
    code: "FK",
    dial_code: "+500",
    name: "Falkland Islands",
    flag: "🇫🇰",
    currency: "FKP",
  },
  {
    code: "FO",
    dial_code: "+298",
    name: "Faroe Islands",
    flag: "🇫🇴",
    currency: "DKK",
  },
  { code: "FJ", dial_code: "+679", name: "Fiji", flag: "🇫🇯", currency: "FJD" },
  {
    code: "FI",
    dial_code: "+358",
    name: "Finland",
    flag: "🇫🇮",
    currency: "EUR",
  },
  { code: "FR", dial_code: "+33", name: "France", flag: "🇫🇷", currency: "EUR" },
  {
    code: "GF",
    dial_code: "+594",
    name: "French Guiana",
    flag: "🇬🇫",
    currency: "EUR",
  },
  {
    code: "PF",
    dial_code: "+689",
    name: "French Polynesia",
    flag: "🇵🇫",
    currency: "XPF",
  },
  { code: "GA", dial_code: "+241", name: "Gabon", flag: "🇬🇦", currency: "XAF" },
  {
    code: "GM",
    dial_code: "+220",
    name: "Gambia",
    flag: "🇬🇲",
    currency: "GMD",
  },
  {
    code: "GE",
    dial_code: "+995",
    name: "Georgia",
    flag: "🇬🇪",
    currency: "GEL",
  },
  {
    code: "DE",
    dial_code: "+49",
    name: "Germany",
    flag: "🇩🇪",
    currency: "EUR",
  },
  { code: "GH", dial_code: "+233", name: "Ghana", flag: "🇬🇭", currency: "GHS" },
  {
    code: "GI",
    dial_code: "+350",
    name: "Gibraltar",
    flag: "🇬🇮",
    currency: "GIP",
  },
  { code: "GR", dial_code: "+30", name: "Greece", flag: "🇬🇷", currency: "EUR" },
  {
    code: "GL",
    dial_code: "+299",
    name: "Greenland",
    flag: "🇬🇱",
    currency: "DKK",
  },
  {
    code: "GD",
    dial_code: "+1473",
    name: "Grenada",
    flag: "🇬🇩",
    currency: "XCD",
  },
  {
    code: "GP",
    dial_code: "+590",
    name: "Guadeloupe",
    flag: "🇬🇵",
    currency: "EUR",
  },
  { code: "GU", dial_code: "+1671", name: "Guam", flag: "🇬🇺", currency: "USD" },
  {
    code: "GT",
    dial_code: "+502",
    name: "Guatemala",
    flag: "🇬🇹",
    currency: "GTQ",
  },
  {
    code: "GG",
    dial_code: "+44",
    name: "Guernsey",
    flag: "🇬🇬",
    currency: "GBP",
  },
  {
    code: "GN",
    dial_code: "+224",
    name: "Guinea",
    flag: "🇬🇳",
    currency: "GNF",
  },
  {
    code: "GW",
    dial_code: "+245",
    name: "Guinea-Bissau",
    flag: "🇬🇼",
    currency: "XOF",
  },
  {
    code: "GY",
    dial_code: "+592",
    name: "Guyana",
    flag: "🇬🇾",
    currency: "GYD",
  },
  { code: "HT", dial_code: "+509", name: "Haiti", flag: "🇭🇹", currency: "HTG" },
  {
    code: "VA",
    dial_code: "+379",
    name: "Vatican City",
    flag: "🇻🇦",
    currency: "EUR",
  },
  {
    code: "HN",
    dial_code: "+504",
    name: "Honduras",
    flag: "🇭🇳",
    currency: "HNL",
  },
  {
    code: "HK",
    dial_code: "+852",
    name: "Hong Kong",
    flag: "🇭🇰",
    currency: "HKD",
  },
  {
    code: "HU",
    dial_code: "+36",
    name: "Hungary",
    flag: "🇭🇺",
    currency: "HUF",
  },
  {
    code: "IS",
    dial_code: "+354",
    name: "Iceland",
    flag: "🇮🇸",
    currency: "ISK",
  },
  { code: "IN", dial_code: "+91", name: "India", flag: "🇮🇳", currency: "INR" },
  {
    code: "ID",
    dial_code: "+62",
    name: "Indonesia",
    flag: "🇮🇩",
    currency: "IDR",
  },
  { code: "IR", dial_code: "+98", name: "Iran", flag: "🇮🇷", currency: "IRR" },
  { code: "IQ", dial_code: "+964", name: "Iraq", flag: "🇮🇶", currency: "IQD" },
  {
    code: "IE",
    dial_code: "+353",
    name: "Ireland",
    flag: "🇮🇪",
    currency: "EUR",
  },
  {
    code: "IM",
    dial_code: "+44",
    name: "Isle of Man",
    flag: "🇮🇲",
    currency: "GBP",
  },
  {
    code: "IL",
    dial_code: "+972",
    name: "Israel",
    flag: "🇮🇱",
    currency: "ILS",
  },
  { code: "IT", dial_code: "+39", name: "Italy", flag: "🇮🇹", currency: "EUR" },
  {
    code: "JM",
    dial_code: "+1876",
    name: "Jamaica",
    flag: "🇯🇲",
    currency: "JMD",
  },
  { code: "JP", dial_code: "+81", name: "Japan", flag: "🇯🇵", currency: "JPY" },
  { code: "JE", dial_code: "+44", name: "Jersey", flag: "🇯🇪", currency: "GBP" },
  {
    code: "JO",
    dial_code: "+962",
    name: "Jordan",
    flag: "🇯🇴",
    currency: "JOD",
  },
  {
    code: "KZ",
    dial_code: "+7",
    name: "Kazakhstan",
    flag: "🇰🇿",
    currency: "KZT",
  },
  { code: "KE", dial_code: "+254", name: "Kenya", flag: "🇰🇪", currency: "KES" },
  {
    code: "KI",
    dial_code: "+686",
    name: "Kiribati",
    flag: "🇰🇮",
    currency: "AUD",
  },
  {
    code: "KP",
    dial_code: "+850",
    name: "North Korea",
    flag: "🇰🇵",
    currency: "KPW",
  },
  {
    code: "KR",
    dial_code: "+82",
    name: "South Korea",
    flag: "🇰🇷",
    currency: "KRW",
  },
  {
    code: "KW",
    dial_code: "+965",
    name: "Kuwait",
    flag: "🇰🇼",
    currency: "KWD",
  },
  {
    code: "KG",
    dial_code: "+996",
    name: "Kyrgyzstan",
    flag: "🇰🇬",
    currency: "KGS",
  },
  { code: "LA", dial_code: "+856", name: "Laos", flag: "🇱🇦", currency: "LAK" },
  {
    code: "LV",
    dial_code: "+371",
    name: "Latvia",
    flag: "🇱🇻",
    currency: "EUR",
  },
  {
    code: "LB",
    dial_code: "+961",
    name: "Lebanon",
    flag: "🇱🇧",
    currency: "LBP",
  },
  {
    code: "LS",
    dial_code: "+266",
    name: "Lesotho",
    flag: "🇱🇸",
    currency: "LSL",
  },
  {
    code: "LR",
    dial_code: "+231",
    name: "Liberia",
    flag: "🇱🇷",
    currency: "LRD",
  },
  { code: "LY", dial_code: "+218", name: "Libya", flag: "🇱🇾", currency: "LYD" },
  {
    code: "LI",
    dial_code: "+423",
    name: "Liechtenstein",
    flag: "🇱🇮",
    currency: "CHF",
  },
  {
    code: "LT",
    dial_code: "+370",
    name: "Lithuania",
    flag: "🇱🇹",
    currency: "EUR",
  },
  {
    code: "LU",
    dial_code: "+352",
    name: "Luxembourg",
    flag: "🇱🇺",
    currency: "EUR",
  },
  { code: "MO", dial_code: "+853", name: "Macao", flag: "🇲🇴", currency: "MOP" },
  {
    code: "MK",
    dial_code: "+389",
    name: "North Macedonia",
    flag: "🇲🇰",
    currency: "MKD",
  },
  {
    code: "MG",
    dial_code: "+261",
    name: "Madagascar",
    flag: "🇲🇬",
    currency: "MGA",
  },
  {
    code: "MW",
    dial_code: "+265",
    name: "Malawi",
    flag: "🇲🇼",
    currency: "MWK",
  },
  {
    code: "MY",
    dial_code: "+60",
    name: "Malaysia",
    flag: "🇲🇾",
    currency: "MYR",
  },
  {
    code: "MV",
    dial_code: "+960",
    name: "Maldives",
    flag: "🇲🇻",
    currency: "MVR",
  },
  { code: "ML", dial_code: "+223", name: "Mali", flag: "🇲🇱", currency: "XOF" },
  { code: "MT", dial_code: "+356", name: "Malta", flag: "🇲🇹", currency: "EUR" },
  {
    code: "MH",
    dial_code: "+692",
    name: "Marshall Islands",
    flag: "🇲🇭",
    currency: "USD",
  },
  {
    code: "MQ",
    dial_code: "+596",
    name: "Martinique",
    flag: "🇲🇶",
    currency: "EUR",
  },
  {
    code: "MR",
    dial_code: "+222",
    name: "Mauritania",
    flag: "🇲🇷",
    currency: "MRU",
  },
  {
    code: "MU",
    dial_code: "+230",
    name: "Mauritius",
    flag: "🇲🇺",
    currency: "MUR",
  },
  {
    code: "YT",
    dial_code: "+262",
    name: "Mayotte",
    flag: "🇾🇹",
    currency: "EUR",
  },
  { code: "MX", dial_code: "+52", name: "Mexico", flag: "🇲🇽", currency: "MXN" },
  {
    code: "FM",
    dial_code: "+691",
    name: "Micronesia",
    flag: "🇫🇲",
    currency: "USD",
  },
  {
    code: "MD",
    dial_code: "+373",
    name: "Moldova",
    flag: "🇲🇩",
    currency: "MDL",
  },
  {
    code: "MC",
    dial_code: "+377",
    name: "Monaco",
    flag: "🇲🇨",
    currency: "EUR",
  },
  {
    code: "MN",
    dial_code: "+976",
    name: "Mongolia",
    flag: "🇲🇳",
    currency: "MNT",
  },
  {
    code: "ME",
    dial_code: "+382",
    name: "Montenegro",
    flag: "🇲🇪",
    currency: "EUR",
  },
  {
    code: "MS",
    dial_code: "+1664",
    name: "Montserrat",
    flag: "🇲🇸",
    currency: "XCD",
  },
  {
    code: "MA",
    dial_code: "+212",
    name: "Morocco",
    flag: "🇲🇦",
    currency: "MAD",
  },
  {
    code: "MZ",
    dial_code: "+258",
    name: "Mozambique",
    flag: "🇲🇿",
    currency: "MZN",
  },
  {
    code: "MM",
    dial_code: "+95",
    name: "Myanmar",
    flag: "🇲🇲",
    currency: "MMK",
  },
  {
    code: "NA",
    dial_code: "+264",
    name: "Namibia",
    flag: "🇳🇦",
    currency: "NAD",
  },
  { code: "NR", dial_code: "+674", name: "Nauru", flag: "🇳🇷", currency: "AUD" },
  { code: "NP", dial_code: "+977", name: "Nepal", flag: "🇳🇵", currency: "NPR" },
  {
    code: "NL",
    dial_code: "+31",
    name: "Netherlands",
    flag: "🇳🇱",
    currency: "EUR",
  },
  {
    code: "NC",
    dial_code: "+687",
    name: "New Caledonia",
    flag: "🇳🇨",
    currency: "XPF",
  },
  {
    code: "NZ",
    dial_code: "+64",
    name: "New Zealand",
    flag: "🇳🇿",
    currency: "NZD",
  },
  {
    code: "NI",
    dial_code: "+505",
    name: "Nicaragua",
    flag: "🇳🇮",
    currency: "NIO",
  },
  { code: "NE", dial_code: "+227", name: "Niger", flag: "🇳🇪", currency: "XOF" },
  {
    code: "NG",
    dial_code: "+234",
    name: "Nigeria",
    flag: "🇳🇬",
    currency: "NGN",
  },
  { code: "NU", dial_code: "+683", name: "Niue", flag: "🇳🇺", currency: "NZD" },
  {
    code: "NF",
    dial_code: "+672",
    name: "Norfolk Island",
    flag: "🇳🇫",
    currency: "AUD",
  },
  {
    code: "MP",
    dial_code: "+1670",
    name: "Northern Mariana Islands",
    flag: "🇲🇵",
    currency: "USD",
  },
  { code: "NO", dial_code: "+47", name: "Norway", flag: "🇳🇴", currency: "NOK" },
  { code: "OM", dial_code: "+968", name: "Oman", flag: "🇴🇲", currency: "OMR" },
  {
    code: "PK",
    dial_code: "+92",
    name: "Pakistan",
    flag: "🇵🇰",
    currency: "PKR",
  },
  { code: "PW", dial_code: "+680", name: "Palau", flag: "🇵🇼", currency: "USD" },
  {
    code: "PS",
    dial_code: "+970",
    name: "Palestine",
    flag: "🇵🇸",
    currency: "ILS",
  },
  {
    code: "PA",
    dial_code: "+507",
    name: "Panama",
    flag: "🇵🇦",
    currency: "PAB",
  },
  {
    code: "PG",
    dial_code: "+675",
    name: "Papua New Guinea",
    flag: "🇵🇬",
    currency: "PGK",
  },
  {
    code: "PY",
    dial_code: "+595",
    name: "Paraguay",
    flag: "🇵🇾",
    currency: "PYG",
  },
  { code: "PE", dial_code: "+51", name: "Peru", flag: "🇵🇪", currency: "PEN" },
  {
    code: "PH",
    dial_code: "+63",
    name: "Philippines",
    flag: "🇵🇭",
    currency: "PHP",
  },
  {
    code: "PN",
    dial_code: "+872",
    name: "Pitcairn",
    flag: "🇵🇳",
    currency: "NZD",
  },
  { code: "PL", dial_code: "+48", name: "Poland", flag: "🇵🇱", currency: "PLN" },
  {
    code: "PT",
    dial_code: "+351",
    name: "Portugal",
    flag: "🇵🇹",
    currency: "EUR",
  },
  {
    code: "PR",
    dial_code: "+1939",
    name: "Puerto Rico",
    flag: "🇵🇷",
    currency: "USD",
  },
  { code: "QA", dial_code: "+974", name: "Qatar", flag: "🇶🇦", currency: "QAR" },
  {
    code: "RE",
    dial_code: "+262",
    name: "Réunion",
    flag: "🇷🇪",
    currency: "EUR",
  },
  {
    code: "RO",
    dial_code: "+40",
    name: "Romania",
    flag: "🇷🇴",
    currency: "RON",
  },
  { code: "RU", dial_code: "+7", name: "Russia", flag: "🇷🇺", currency: "RUB" },
  {
    code: "RW",
    dial_code: "+250",
    name: "Rwanda",
    flag: "🇷🇼",
    currency: "RWF",
  },
  {
    code: "BL",
    dial_code: "+590",
    name: "Saint Barthélemy",
    flag: "🇧🇱",
    currency: "EUR",
  },
  {
    code: "SH",
    dial_code: "+290",
    name: "Saint Helena",
    flag: "🇸🇭",
    currency: "SHP",
  },
  {
    code: "KN",
    dial_code: "+1869",
    name: "Saint Kitts and Nevis",
    flag: "🇰🇳",
    currency: "XCD",
  },
  {
    code: "LC",
    dial_code: "+1758",
    name: "Saint Lucia",
    flag: "🇱🇨",
    currency: "XCD",
  },
  {
    code: "MF",
    dial_code: "+590",
    name: "Saint Martin",
    flag: "🇲🇫",
    currency: "EUR",
  },
  {
    code: "PM",
    dial_code: "+508",
    name: "Saint Pierre and Miquelon",
    flag: "🇵🇲",
    currency: "EUR",
  },
  {
    code: "VC",
    dial_code: "+1784",
    name: "Saint Vincent and the Grenadines",
    flag: "🇻🇨",
    currency: "XCD",
  },
  { code: "WS", dial_code: "+685", name: "Samoa", flag: "🇼🇸", currency: "WST" },
  {
    code: "SM",
    dial_code: "+378",
    name: "San Marino",
    flag: "🇸🇲",
    currency: "EUR",
  },
  {
    code: "ST",
    dial_code: "+239",
    name: "São Tomé and Príncipe",
    flag: "🇸🇹",
    currency: "STN",
  },
  {
    code: "SA",
    dial_code: "+966",
    name: "Saudi Arabia",
    flag: "🇸🇦",
    currency: "SAR",
  },
  {
    code: "SN",
    dial_code: "+221",
    name: "Senegal",
    flag: "🇸🇳",
    currency: "XOF",
  },
  {
    code: "RS",
    dial_code: "+381",
    name: "Serbia",
    flag: "🇷🇸",
    currency: "RSD",
  },
  {
    code: "SC",
    dial_code: "+248",
    name: "Seychelles",
    flag: "🇸🇨",
    currency: "SCR",
  },
  {
    code: "SL",
    dial_code: "+232",
    name: "Sierra Leone",
    flag: "🇸🇱",
    currency: "SLE",
  },
  {
    code: "SG",
    dial_code: "+65",
    name: "Singapore",
    flag: "🇸🇬",
    currency: "SGD",
  },
  {
    code: "SX",
    dial_code: "+1721",
    name: "Sint Maarten",
    flag: "🇸🇽",
    currency: "ANG",
  },
  {
    code: "SK",
    dial_code: "+421",
    name: "Slovakia",
    flag: "🇸🇰",
    currency: "EUR",
  },
  {
    code: "SI",
    dial_code: "+386",
    name: "Slovenia",
    flag: "🇸🇮",
    currency: "EUR",
  },
  {
    code: "SB",
    dial_code: "+677",
    name: "Solomon Islands",
    flag: "🇸🇧",
    currency: "SBD",
  },
  {
    code: "SO",
    dial_code: "+252",
    name: "Somalia",
    flag: "🇸🇴",
    currency: "SOS",
  },
  {
    code: "ZA",
    dial_code: "+27",
    name: "South Africa",
    flag: "🇿🇦",
    currency: "ZAR",
  },
  {
    code: "SS",
    dial_code: "+211",
    name: "South Sudan",
    flag: "🇸🇸",
    currency: "SSP",
  },
  {
    code: "GS",
    dial_code: "+500",
    name: "South Georgia",
    flag: "🇬🇸",
    currency: "GBP",
  },
  { code: "ES", dial_code: "+34", name: "Spain", flag: "🇪🇸", currency: "EUR" },
  {
    code: "LK",
    dial_code: "+94",
    name: "Sri Lanka",
    flag: "🇱🇰",
    currency: "LKR",
  },
  { code: "SD", dial_code: "+249", name: "Sudan", flag: "🇸🇩", currency: "SDG" },
  {
    code: "SR",
    dial_code: "+597",
    name: "Suriname",
    flag: "🇸🇷",
    currency: "SRD",
  },
  {
    code: "SJ",
    dial_code: "+47",
    name: "Svalbard and Jan Mayen",
    flag: "🇸🇯",
    currency: "NOK",
  },
  {
    code: "SZ",
    dial_code: "+268",
    name: "Eswatini",
    flag: "🇸🇿",
    currency: "SZL",
  },
  { code: "SE", dial_code: "+46", name: "Sweden", flag: "🇸🇪", currency: "SEK" },
  {
    code: "CH",
    dial_code: "+41",
    name: "Switzerland",
    flag: "🇨🇭",
    currency: "CHF",
  },
  { code: "SY", dial_code: "+963", name: "Syria", flag: "🇸🇾", currency: "SYP" },
  {
    code: "TW",
    dial_code: "+886",
    name: "Taiwan",
    flag: "🇹🇼",
    currency: "TWD",
  },
  {
    code: "TJ",
    dial_code: "+992",
    name: "Tajikistan",
    flag: "🇹🇯",
    currency: "TJS",
  },
  {
    code: "TZ",
    dial_code: "+255",
    name: "Tanzania",
    flag: "🇹🇿",
    currency: "TZS",
  },
  {
    code: "TH",
    dial_code: "+66",
    name: "Thailand",
    flag: "🇹🇭",
    currency: "THB",
  },
  {
    code: "TL",
    dial_code: "+670",
    name: "Timor-Leste",
    flag: "🇹🇱",
    currency: "USD",
  },
  { code: "TG", dial_code: "+228", name: "Togo", flag: "🇹🇬", currency: "XOF" },
  {
    code: "TK",
    dial_code: "+690",
    name: "Tokelau",
    flag: "🇹🇰",
    currency: "NZD",
  },
  { code: "TO", dial_code: "+676", name: "Tonga", flag: "🇹🇴", currency: "TOP" },
  {
    code: "TT",
    dial_code: "+1868",
    name: "Trinidad and Tobago",
    flag: "🇹🇹",
    currency: "TTD",
  },
  {
    code: "TN",
    dial_code: "+216",
    name: "Tunisia",
    flag: "🇹🇳",
    currency: "TND",
  },
  { code: "TR", dial_code: "+90", name: "Turkey", flag: "🇹🇷", currency: "TRY" },
  {
    code: "TM",
    dial_code: "+993",
    name: "Turkmenistan",
    flag: "🇹🇲",
    currency: "TMT",
  },
  {
    code: "TC",
    dial_code: "+1649",
    name: "Turks and Caicos Islands",
    flag: "🇹🇨",
    currency: "USD",
  },
  {
    code: "TV",
    dial_code: "+688",
    name: "Tuvalu",
    flag: "🇹🇻",
    currency: "AUD",
  },
  {
    code: "UG",
    dial_code: "+256",
    name: "Uganda",
    flag: "🇺🇬",
    currency: "UGX",
  },
  {
    code: "UA",
    dial_code: "+380",
    name: "Ukraine",
    flag: "🇺🇦",
    currency: "UAH",
  },
  {
    code: "AE",
    dial_code: "+971",
    name: "United Arab Emirates",
    flag: "🇦🇪",
    currency: "AED",
  },
  {
    code: "GB",
    dial_code: "+44",
    name: "United Kingdom",
    flag: "🇬🇧",
    currency: "GBP",
  },
  {
    code: "US",
    dial_code: "+1",
    name: "United States",
    flag: "🇺🇸",
    currency: "USD",
  },
  {
    code: "UY",
    dial_code: "+598",
    name: "Uruguay",
    flag: "🇺🇾",
    currency: "UYU",
  },
  {
    code: "UZ",
    dial_code: "+998",
    name: "Uzbekistan",
    flag: "🇺🇿",
    currency: "UZS",
  },
  {
    code: "VU",
    dial_code: "+678",
    name: "Vanuatu",
    flag: "🇻🇺",
    currency: "VUV",
  },
  {
    code: "VE",
    dial_code: "+58",
    name: "Venezuela",
    flag: "🇻🇪",
    currency: "VES",
  },
  {
    code: "VN",
    dial_code: "+84",
    name: "Vietnam",
    flag: "🇻🇳",
    currency: "VND",
  },
  {
    code: "VG",
    dial_code: "+1284",
    name: "British Virgin Islands",
    flag: "🇻🇬",
    currency: "USD",
  },
  {
    code: "VI",
    dial_code: "+1340",
    name: "U.S. Virgin Islands",
    flag: "🇻🇮",
    currency: "USD",
  },
  {
    code: "WF",
    dial_code: "+681",
    name: "Wallis and Futuna",
    flag: "🇼🇫",
    currency: "XPF",
  },
  {
    code: "EH",
    dial_code: "+212",
    name: "Western Sahara",
    flag: "🇪🇭",
    currency: "MAD",
  },
  { code: "YE", dial_code: "+967", name: "Yemen", flag: "🇾🇪", currency: "YER" },
  {
    code: "ZM",
    dial_code: "+260",
    name: "Zambia",
    flag: "🇿🇲",
    currency: "ZMW",
  },
  {
    code: "ZW",
    dial_code: "+263",
    name: "Zimbabwe",
    flag: "🇿🇼",
    currency: "ZWL",
  },
];

/**
 * Validation result for phone number
 */
export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate a phone number based on country code using libphonenumber-js
 * @param phoneNumber - The national phone number (without country code)
 * @param countryCode - The ISO country code (e.g., "IN", "US") or dial code (e.g., "+91", "+1")
 * @returns Validation result with isValid flag and optional error message
 */
export const validatePhoneByCountry = (
  phoneNumber: string,
  countryCode: string,
): PhoneValidationResult => {
  if (!phoneNumber || phoneNumber.trim() === "") {
    return { isValid: true }; // Empty is valid (required check is separate)
  }

  // Only allow digits in the phone number
  if (!/^\d+$/.test(phoneNumber)) {
    return { isValid: false, error: "Only numbers are allowed" };
  }

  try {
    // Get the ISO country code from dial code if necessary
    let isoCode = countryCode;
    if (countryCode.startsWith("+")) {
      const country = getCountryByDialCode(countryCode);
      if (country) {
        isoCode = country.code;
      } else {
        // If we can't find the country, just do basic length check
        return phoneNumber.length >= 4 && phoneNumber.length <= 15
          ? { isValid: true }
          : { isValid: false, error: "Invalid phone number length" };
      }
    }

    // Parse and validate using libphonenumber-js
    const fullNumber = `${countryCode.startsWith("+") ? countryCode : "+" + countryCode}${phoneNumber}`;
    const parsed = parsePhoneNumber(fullNumber, isoCode as any);

    if (parsed && parsed.isValid()) {
      return { isValid: true };
    }

    // Get expected length for the country for better error message
    const expectedLength = parsed?.nationalNumber?.length;
    if (expectedLength && phoneNumber.length !== expectedLength) {
      return {
        isValid: false,
        error: `Phone number should be ${expectedLength} digits for this country`,
      };
    }

    return { isValid: false, error: "Invalid phone number for this country" };
  } catch {
    // Fallback: basic validation if parsing fails
    // Most phone numbers are between 4-15 digits
    if (phoneNumber.length < 4) {
      return { isValid: false, error: "Phone number is too short" };
    }
    if (phoneNumber.length > 15) {
      return { isValid: false, error: "Phone number is too long" };
    }
    return { isValid: true };
  }
};
